import { EntityNotDownloadedError } from 'shared/channels/entity';
import type { ServiceData } from 'serviceWorker/db/service';
import { logItemNotFoundById } from 'serviceWorker/helpers/dataLog';
import type { Message } from 'shared/domain/messages/message';
import {
  isPullAboutToStart,
  isPullNotAllowed,
  isPullParkedForConfig,
  markPullStep,
  PullStep,
  readPullStep,
} from 'serviceWorker/services/factories/pullProgress';
import { isPullRunning } from 'serviceWorker/services/factories/runningPulls';
import { waitForEndingMessage } from 'serviceWorker/services/factories/waitForMessage';
import type { EntityContract } from 'shared/channels/entity';
import type { ChannelContractShape } from 'shared/channels';
import { getPullDiagnostics, isWorkerOffline } from 'serviceWorker/helpers/pullDiagnostics';
import { swLog } from 'serviceWorker/helpers/makeSwLogger';
import { describeEntitySync } from 'shared/domain/serviceData/entitySync';
import { swSignals } from 'serviceWorker/events/swSignals';
import { LogLevel } from 'shared/types/logger';
import { syncWhenRequestsAllowed } from './runWhenRequestsAllowed';
import type { EntityByIdSource, EntitySource } from './serveEntityRequests';

/** Where an entity lives in the worker: its IndexedDB tables, its contract and its sync. */
export interface EntityStorage<TModel> {
  /** Download state of the entity (`<entity>Service` table); `undefined` before the first download. */
  service: { get: () => Promise<ServiceData | undefined>; stopDownloading: () => Promise<void> };
  contract: Pick<EntityContract<TModel, ChannelContractShape['events'], ChannelContractShape['requests']>, 'key' | 'collectionEvent'>;
  /** Requests a debounced sync from the API; does not wait for it. */
  sync: () => void;
  /**
   * Downloads the collection when it is not downloaded, and only then. The
   * first download of a read goes through it: a pull, with its own steps and
   * its wait for the config, instead of an incremental sync that has other
   * ways out before it downloads. Without it the first download is a `sync`.
   * Never rejects.
   */
  downloadWhenMissing?: () => Promise<void>;
  /**
   * Syncs from the API now and resolves once the result is stored. A read by
   * id of an item missing from a downloaded entity waits for it and reads
   * again: the item was created since the last sync (a create the HTTP queue
   * answered without storing it, or another user's). Without it a missing
   * item is rejected at once. Resolves whether the result was stored.
   */
  syncAndWait?: () => Promise<boolean>;
  /**
   * Fetches one item the collection lacks from the API and stores it, for an
   * entity that has an endpoint by id (issues). It never rejects: an item the
   * API does not return, or an API that is not reached, stays unknown. The
   * collection is never downloaded again for a missing item.
   */
  fetchOne?: (id: string) => Promise<void>;
  /** Every item of IndexedDB, mapped to models. */
  readAll: () => Promise<TModel[]>;
  /**
   * Sync after every read of a downloaded collection, as the legacy `getAll`
   * handlers did: changes made elsewhere (other users, the server) and edits
   * the HTTP queue does not store reach the page. Off for field values, whose
   * legacy reads synced only a missing download. Default: on.
   */
  syncAfterRead?: boolean;
  /**
   * Runs after a read instead of `sync`, when reads refresh differently: issues
   * throttle it (the grid reads on every page), projects download only a
   * missing list (`downloadWhenMissing`), while `sync` downloads it again.
   */
  refreshAfterRead?: () => void;
}

/** What reads of an entity need to wait for its download and refresh it afterwards. */
export type EntityReadStorage = Omit<EntityStorage<unknown>, 'readAll'>;

export interface EntityByIdStorage<TModel> extends EntityStorage<TModel> {
  /** One item of IndexedDB by id, archived ones included, mapped to a model. */
  readOne: (id: string) => Promise<TModel | undefined>;
}

/**
 * Whether the first download of the entity is over: it is downloaded, or no
 * download of it runs or is about to start any more (a failed pull). A
 * collection broadcast meanwhile (a sync, a resync or clear after a project
 * flush) does not end it, also while the requested download still waits to
 * start (A11, PT-7171: held back by the config of another project being
 * replaced). Running is the in-memory mark, not the stored `isDownloading` a
 * restarted worker leaves.
 */
async function hasFirstDownloadEnded(storage: EntityReadStorage): Promise<boolean> {
  const { contract } = storage;
  return (await isStoredAsDownloaded(storage)) || !(isPullRunning(contract) || isPullAboutToStart(contract));
}

/** Whether the entity's service table says its collection is downloaded. */
async function isStoredAsDownloaded({ service }: EntityReadStorage): Promise<boolean> {
  return Boolean((await service.get())?.isDownloaded);
}

/** The wait for the first download ended without an event: no download of the entity ran. */
export const FIRST_DOWNLOAD_NOT_RUNNING = 'first-download-not-running' as const;

/** The wait for the first download ended with the session (a logout): no download will run for it. */
export const FIRST_DOWNLOAD_SESSION_ENDED = 'first-download-session-ended' as const;

/**
 * `null`: downloaded before; the message that ended the wait;
 * `FIRST_DOWNLOAD_NOT_RUNNING`; or `FIRST_DOWNLOAD_SESSION_ENDED`.
 */
export type FirstDownloadEnd =
  | Message
  | null
  | typeof FIRST_DOWNLOAD_NOT_RUNNING
  | typeof FIRST_DOWNLOAD_SESSION_ENDED;

/** How often a read waiting for the first download checks that one runs. */
const DOWNLOAD_RUN_CHECK_MS = 1_000;
/**
 * How long a read waits while no download of the entity runs (none started,
 * or one ended without an event) before it answers what is stored; shorter
 * than the page's request timeout, and far above `REQUEST_SUSPENDER_TIMEOUT_MS`:
 * a start held back by a project switch is dropped before this limit passes.
 * A running download is waited for.
 */
export const NO_DOWNLOAD_RUNNING_LIMIT_MS = 15_000;
/**
 * How long a read waits for a pull that is parked until the worker has its
 * config (it goes on by itself once the page configured the worker). Twice
 * the page's bulk read timeout: a retried request of the page is covered, a
 * config that never comes does not hold the waiting reads for ever.
 */
export const PARKED_FOR_CONFIG_LIMIT_MS = 60_000;

/**
 * Resolves `FIRST_DOWNLOAD_NOT_RUNNING` once no download of the entity has run
 * for `NO_DOWNLOAD_RUNNING_LIMIT_MS`: no event will end the wait then. A pull
 * parked until the worker has its config counts as running (for
 * `PARKED_FOR_CONFIG_LIMIT_MS`). A pull refused for the user's access ends it
 * at the next check: nothing will be downloaded. Never resolves otherwise;
 * `signal` stops the checks once the wait is over.
 */
function whenNoDownloadRuns({ contract }: EntityReadStorage, signal: AbortSignal): Promise<typeof FIRST_DOWNLOAD_NOT_RUNNING> {
  return new Promise((resolve) => {
    const waitStartedAt = Date.now();
    let lastRunningAt = waitStartedAt;
    const timer = setInterval(() => {
      const isParkedForConfig =
        isPullParkedForConfig(contract) && Date.now() - waitStartedAt < PARKED_FOR_CONFIG_LIMIT_MS;
      if (isPullRunning(contract) || isParkedForConfig) {
        lastRunningAt = Date.now();
      } else if (isPullNotAllowed(contract) || Date.now() - lastRunningAt >= NO_DOWNLOAD_RUNNING_LIMIT_MS) {
        clearInterval(timer);
        resolve(FIRST_DOWNLOAD_NOT_RUNNING);
      }
    }, DOWNLOAD_RUN_CHECK_MS);
    signal.addEventListener('abort', () => clearInterval(timer), { once: true });
  });
}

interface SessionEndListener {
  /** Resolves `FIRST_DOWNLOAD_SESSION_ENDED` on a logout. */
  ended: Promise<typeof FIRST_DOWNLOAD_SESSION_ENDED>;
  /** Whether the logout already came, also during an await before the wait. */
  hasEnded(): boolean;
}

/** Listens for a logout from the start of a wait; `signal` stops listening once the wait is over. */
function listenForSessionEnd(signal: AbortSignal): SessionEndListener {
  let hasEnded = false;
  const ended = new Promise<typeof FIRST_DOWNLOAD_SESSION_ENDED>((resolve) => {
    const stopListening = swSignals.loggedOut.subscribe(() => {
      hasEnded = true;
      resolve(FIRST_DOWNLOAD_SESSION_ENDED);
    });
    signal.addEventListener('abort', stopListening, { once: true });
  });
  return { ended, hasEnded: () => hasEnded };
}

/** Starts the first download; during a project switch it waits, and it is dropped when operations stay suspended. */
function requestFirstDownload({ contract, sync, downloadWhenMissing }: EntityReadStorage): void {
  markPullStep(contract, PullStep.requested);
  const start = downloadWhenMissing ? (): void => void downloadWhenMissing() : sync;
  syncWhenRequestsAllowed(start, () => markPullStep(contract, PullStep.skippedWhileSuspended));
}

/** The entities whose wait without a download was logged in this run of the worker: one log each. */
const entitiesLoggedWithoutDownload = new Set<string>();
/** The same while the worker is offline: one info each per time without network. */
const entitiesLoggedWhileOffline = new Set<string>();

/**
 * Which log of the entities a wait goes to; `undefined` when the entity's wait
 * was logged already. Without network no download runs as expected: info,
 * once per time without network, and the entity's one error stays for a wait
 * that happens online.
 */
function claimWaitLog(entityKey: string): LogLevel | undefined {
  if (!isWorkerOffline()) {
    entitiesLoggedWhileOffline.clear();
    return claimOnce(entitiesLoggedWithoutDownload, entityKey) ? LogLevel.ERROR : undefined;
  }
  return claimOnce(entitiesLoggedWhileOffline, entityKey) ? LogLevel.INFO : undefined;
}

function claimOnce(logged: Set<string>, entityKey: string): boolean {
  if (logged.has(entityKey)) {
    return false;
  }
  logged.add(entityKey);
  return true;
}

/**
 * Logs that a read stopped waiting. Once per entity per run of the worker: a
 * read the page repeats (every few minutes) adds nothing the first log lacks,
 * and one stuck profile must not be most of the errors.
 */
async function logWaitWithoutDownload({ contract, service }: EntityReadStorage): Promise<void> {
  // An entity the user has no access to is not downloaded on purpose: no error.
  if (isPullNotAllowed(contract)) {
    return;
  }
  const level = claimWaitLog(contract.key);
  if (!level) {
    return;
  }
  swLog('Read stopped waiting for a download that does not run', level, null, {
    entityName: contract.key,
    setup: {
      noDownloadForMs: NO_DOWNLOAD_RUNNING_LIMIT_MS,
      lastPullStep: readPullStep(contract),
      // What the service table says (`never-synced`: no row at all), without the rows.
      serviceState: describeEntitySync({ serviceData: (await service.get().catch(() => null)) ?? null, rowCount: null }),
      ...getPullDiagnostics(),
    },
  });
}

/** test-only */
export function forgetLoggedWaitsWithoutDownloadForTests(): void {
  entitiesLoggedWithoutDownload.clear();
  entitiesLoggedWhileOffline.clear();
}

/**
 * Waits for the first download of the entity, starting it when missing.
 * Resolves `null` when the entity was already downloaded, otherwise with the
 * message that ended the wait (the collection, or `pullFailed` with its
 * reason), `FIRST_DOWNLOAD_NOT_RUNNING` when no download runs, or
 * `FIRST_DOWNLOAD_SESSION_ENDED` at once on a logout (PT-7259). While an
 * incremental sync runs the stored items are served at once, as the legacy
 * reads did, instead of waiting for a sync that may end without an event.
 */
export async function waitForFirstDownload(storage: EntityReadStorage): Promise<FirstDownloadEnd> {
  const { contract } = storage;
  const waitEnded = new AbortController();
  // Before the first await: a logout while the stored state is read ends the wait too.
  const sessionEnd = listenForSessionEnd(waitEnded.signal);
  try {
    if (await isStoredAsDownloaded(storage)) {
      return null;
    }
    if (sessionEnd.hasEnded()) {
      return FIRST_DOWNLOAD_SESSION_ENDED;
    }
    const downloaded = waitForEndingMessage(
      contract,
      contract.collectionEvent,
      () => hasFirstDownloadEnded(storage),
      waitEnded.signal
    );
    requestFirstDownload(storage);
    const end = await Promise.race([
      downloaded,
      whenNoDownloadRuns(storage, waitEnded.signal),
      sessionEnd.ended,
    ]);
    if (end === FIRST_DOWNLOAD_NOT_RUNNING) {
      void logWaitWithoutDownload(storage);
    }
    return end;
  } finally {
    waitEnded.abort();
  }
}

/**
 * Resolves `true` when the entity was already downloaded, `false` after
 * waiting for its first download, also a failed one or one the session ended
 * (at once on a logout): callers read what is stored. Never rejects: the
 * broadcasts and services that read through it rely on that.
 */
export async function ensureDownloaded(storage: EntityReadStorage): Promise<boolean> {
  return (await waitForFirstDownload(storage)) === null;
}

function syncAfterRead(storage: EntityReadStorage, wasDownloaded: boolean): void {
  if (wasDownloaded && storage.syncAfterRead !== false) {
    syncWhenRequestsAllowed(storage.refreshAfterRead ?? storage.sync);
  }
}

/**
 * Runs `read` once the entity is downloaded (a missing download is started
 * first), then refreshes the entity like the legacy reads did. For reads that
 * are not the whole collection, e.g. filtered pages of a large entity.
 * Rejects when the session ended during the wait.
 */
export async function readWhenDownloaded<TResult>(
  storage: EntityReadStorage,
  read: () => Promise<TResult>
): Promise<TResult> {
  const end = await waitForFirstDownload(storage);
  if (end === FIRST_DOWNLOAD_SESSION_ENDED) {
    // The page's read gets the reason at once, not what is left while the user's data is dropped.
    throw new Error('The session ended while waiting for the download');
  }
  const wasDownloaded = end === null;
  const result = await read();
  syncAfterRead(storage, wasDownloaded);
  return result;
}

/** A source that keeps where its entity lives, for checks of the download (project loading). */
export type StoredEntitySource<TModel> = EntitySource<TModel> & { readonly storage: EntityReadStorage };

/** Reads the entity once downloaded; a missing download is started first. */
export function createEntitySource<TModel>(storage: EntityStorage<TModel>): StoredEntitySource<TModel> {
  return {
    getAll: () => readWhenDownloaded(storage, () => storage.readAll()),
    sync: storage.sync,
    storage,
  };
}

/**
 * One item once the entity is downloaded. A stored item is answered at once
 * (also offline) and the entity refreshed afterwards. A missing one (created
 * since the last sync, deleted, or opened from a link before the download
 * ended) waits for `syncAndWait`, which is then the refresh of this read, and
 * is then fetched alone by `fetchOne` when the entity has an endpoint by id:
 * the collection is never cleared or downloaded again for one item. For
 * sources without `readAll` too (issues).
 */
export async function readOneSyncingMissing<TItem>(
  storage: EntityReadStorage,
  id: string,
  readOne: () => Promise<TItem | undefined>
): Promise<TItem | undefined> {
  return (await readOneOfDownloaded(storage, id, readOne)).item;
}

/** As `readOneSyncingMissing`, telling whether the entity is downloaded (a missing item then does not exist). */
async function readOneOfDownloaded<TItem>(
  storage: EntityReadStorage,
  id: string,
  readOne: () => Promise<TItem | undefined>
): Promise<{ item: TItem | undefined; isDownloaded: boolean }> {
  const wasDownloaded = await ensureDownloaded(storage);
  const storedItem = await readOne();
  // A first download that just ended counts too: only a failed one leaves the item unknown.
  const isDownloaded = wasDownloaded || (await isStoredAsDownloaded(storage));
  if (storedItem || !isDownloaded) {
    syncAfterRead(storage, wasDownloaded);
    return { item: storedItem, isDownloaded };
  }
  return { item: await findMissingItem(storage, id, readOne, wasDownloaded), isDownloaded: true };
}

/**
 * An item missing from a downloaded entity: looked for in the changes since
 * the last sync (a first download that just ended has them all), then
 * fetched alone. Never by downloading the collection again.
 */
async function findMissingItem<TItem>(
  storage: EntityReadStorage,
  id: string,
  readOne: () => Promise<TItem | undefined>,
  wasDownloaded: boolean
): Promise<TItem | undefined> {
  if (wasDownloaded) {
    await syncForMissingItem(storage);
  }
  const item = (await readOne()) ?? (await fetchAndReadOne(storage, id, readOne));
  if (!item) {
    logItemNotFoundById(storage.contract.key, id);
  }
  return item;
}

/** Waits for the sync when the storage can; otherwise only asks for one, as after any read. */
async function syncForMissingItem(storage: EntityReadStorage): Promise<void> {
  if (storage.syncAndWait) {
    await storage.syncAndWait();
    return;
  }
  syncAfterRead(storage, true);
}

/** The item fetched alone from the API, when the entity has an endpoint by id. */
async function fetchAndReadOne<TItem>(
  storage: EntityReadStorage,
  id: string,
  readOne: () => Promise<TItem | undefined>
): Promise<TItem | undefined> {
  if (!storage.fetchOne) {
    return undefined;
  }
  await storage.fetchOne(id);
  return readOne();
}

/**
 * Like `createEntitySource`, plus one item by id for details views. As the
 * legacy `getOne`, it includes archived items and refreshes the entity from
 * the API; the read comes first, so it also works offline, and the page gets
 * the refreshed item as an event. An item missing from IndexedDB is synced
 * before answering, as the legacy `getOne` did.
 */
export function createEntityByIdSource<TModel>(
  storage: EntityByIdStorage<TModel>
): EntityByIdSource<TModel> & StoredEntitySource<TModel> {
  return {
    ...createEntitySource(storage),
    async getOneById(id) {
      const { item, isDownloaded } = await readOneOfDownloaded(storage, id, () => storage.readOne(id));
      if (item) {
        return item;
      }
      throw isDownloaded
        ? new Error(`${storage.contract.key}: no item with id ${id}`)
        : new EntityNotDownloadedError(`${storage.contract.key}: not downloaded, item ${id} unknown`);
    },
  };
}
